import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';

import { aiApi } from '../../../api/ai.js';
import { ApiError } from '../../../api/client.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';
import { toolLabel } from '../../../lib/toolLabels.js';

function ToolGrid({ toolCatalog, selected, disabled, onToggle }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {toolCatalog.map((tool) => {
        const on = selected.includes(tool.name);
        return (
          <label key={tool.name} className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors ${on ? 'border-ink/30 bg-surface-subtle' : 'border-border bg-surface'} ${disabled ? 'cursor-default opacity-80' : ''}`}>
            <span className="min-w-0">
              <span className="block text-sm text-ink">{toolLabel(tool.name)}</span>
              <span className="block truncate text-xs text-ink-faint" title={tool.description}>{tool.name}</span>
            </span>
            <Switch checked={on} disabled={disabled} onChange={() => onToggle(tool.name)} />
          </label>
        );
      })}
    </div>
  );
}

function AgentCard({ agent, toolCatalog, chatModels, defaultModelId, canManage, onSaved, onDelete }) {
  const isCustom = agent.builtin === false;
  const customPrompt = agent.systemPrompt && agent.systemPrompt !== agent.defaultSystemPrompt;

  const [label, setLabel] = useState(agent.label || '');
  const [description, setDescription] = useState(agent.description || '');
  const [enabled, setEnabled] = useState(agent.enabled);
  const [routable, setRoutable] = useState(agent.routable !== false);
  const [tools, setTools] = useState(agent.tools || []);
  const [model, setModel] = useState(agent.model || '');
  const [temperature, setTemperature] = useState(agent.temperature == null ? '' : String(agent.temperature));
  // Built-in agents: empty prompt means "use the code default". Custom agents:
  // the prompt is the agent's own base, so show it verbatim.
  const [systemPrompt, setSystemPrompt] = useState(isCustom ? (agent.systemPrompt || '') : (customPrompt ? agent.systemPrompt : ''));
  const [showDefault, setShowDefault] = useState(false);

  useEffect(() => {
    const custom = agent.systemPrompt && agent.systemPrompt !== agent.defaultSystemPrompt;
    setLabel(agent.label || '');
    setDescription(agent.description || '');
    setEnabled(agent.enabled);
    setRoutable(agent.routable !== false);
    setTools(agent.tools || []);
    setModel(agent.model || '');
    setTemperature(agent.temperature == null ? '' : String(agent.temperature));
    setSystemPrompt(agent.builtin === false ? (agent.systemPrompt || '') : (custom ? agent.systemPrompt : ''));
  }, [agent]);

  const mutation = useMutation({
    mutationFn: async () => {
      const temp = temperature === '' ? null : Number(temperature);
      if (isCustom) {
        await aiApi.updateAgentDefinition(agent.id, {
          label, description, systemPrompt, tools, model: model || '', temperature: temp, routable
        });
        if (enabled !== agent.enabled) await aiApi.setAgent(agent.id, { enabled });
      } else {
        await aiApi.setAgent(agent.id, {
          enabled, tools, model: model || '', temperature: temp, systemPrompt: systemPrompt || ''
        });
      }
    },
    onSuccess: () => { toast.success(`"${label}" salvato.`); onSaved(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const toggleTool = (name) => setTools((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));

  const initial = {
    label: agent.label || '',
    description: agent.description || '',
    enabled: agent.enabled,
    routable: agent.routable !== false,
    tools: (agent.tools || []).slice().sort().join(','),
    model: agent.model || '',
    temperature: agent.temperature == null ? '' : String(agent.temperature),
    systemPrompt: isCustom ? (agent.systemPrompt || '') : (customPrompt ? agent.systemPrompt : '')
  };
  const dirty = enabled !== initial.enabled
    || routable !== initial.routable
    || (isCustom && label !== initial.label)
    || (isCustom && description !== initial.description)
    || tools.slice().sort().join(',') !== initial.tools
    || model !== initial.model
    || temperature !== initial.temperature
    || systemPrompt !== initial.systemPrompt;

  return (
    <Card>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink">{agent.label}</h2>
            <Badge tone={isCustom ? 'info' : 'neutral'}>{isCustom ? 'personalizzato' : 'predefinito'}</Badge>
            {!enabled && <Badge tone="neutral">disattivato</Badge>}
          </div>
          <p className="text-xs text-ink-muted">{agent.description || 'Nessuna descrizione.'}</p>
          <p className="mt-0.5 text-xs text-ink-faint">id: <code>{agent.id}</code></p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isCustom && canManage && (
            <button type="button" title="Elimina agente" className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger" onClick={() => onDelete(agent)}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <span className="text-xs text-ink-muted">Attivo</span>
          <Switch checked={enabled} disabled={!canManage} onChange={setEnabled} />
        </div>
      </header>

      {isCustom && (
        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Nome" value={label} disabled={!canManage} onChange={(e) => setLabel(e.target.value)} />
          <Input label="Descrizione" value={description} disabled={!canManage} onChange={(e) => setDescription(e.target.value)} hint="Usata dall'orchestratore per decidere quando impiegarlo." />
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select label="Modello" value={model} disabled={!canManage} onChange={(e) => setModel(e.target.value)} hint="Modello usato da questo agente.">
          <option value="">Predefinito{defaultModelId ? ` (${defaultModelId})` : ''}</option>
          {chatModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </Select>
        <Input label="Temperatura" type="number" min="0" max="2" step="0.1" placeholder="Predefinita" value={temperature} disabled={!canManage} onChange={(e) => setTemperature(e.target.value)} hint="0 = preciso, 2 = creativo. Vuoto = predefinita." />
      </div>

      <div className="mb-5">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Istruzioni dell'agente (system prompt)</span>
          {!isCustom && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setShowDefault((v) => !v)}>
              {showDefault ? 'Nascondi predefinito' : 'Mostra predefinito'}
            </button>
          )}
        </div>
        <Textarea rows={5} placeholder={isCustom ? 'Istruzioni operative di questo agente.' : "Lascia vuoto per usare le istruzioni predefinite di questo agente."} value={systemPrompt} disabled={!canManage} onChange={(e) => setSystemPrompt(e.target.value)} />
        {!isCustom && showDefault && (
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-3 text-xs text-ink-muted">{agent.defaultSystemPrompt || '-'}</pre>
        )}
      </div>

      <div className="mb-5 flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
        <span className="min-w-0">
          <span className="block text-sm text-ink">Instradabile dall'orchestratore</span>
          <span className="block text-xs text-ink-faint">Se disattivo, l'agente è raggiungibile solo con selezione esplicita.</span>
        </span>
        <Switch checked={routable} disabled={!canManage} onChange={setRoutable} />
      </div>

      <p className="mb-2 text-sm font-medium text-ink">Abilità (strumenti)</p>
      <ToolGrid toolCatalog={toolCatalog} selected={tools} disabled={!canManage} onToggle={toggleTool} />

      {canManage && (
        <div className="mt-4 flex justify-end">
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!dirty}>Salva</Button>
        </div>
      )}
    </Card>
  );
}

const EMPTY_DRAFT = { id: '', label: '', description: '', systemPrompt: '', tools: [], model: '', temperature: '', routable: true, enabled: true };

function CreateAgentModal({ open, onClose, toolCatalog, chatModels, defaultModelId, onCreated }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  useEffect(() => { if (open) setDraft(EMPTY_DRAFT); }, [open]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const toggleTool = (name) => setDraft((d) => ({ ...d, tools: d.tools.includes(name) ? d.tools.filter((x) => x !== name) : [...d.tools, name] }));

  const mutation = useMutation({
    mutationFn: () => aiApi.createAgent({
      id: draft.id.trim(),
      label: draft.label.trim(),
      description: draft.description.trim(),
      systemPrompt: draft.systemPrompt,
      tools: draft.tools,
      model: draft.model || '',
      temperature: draft.temperature === '' ? null : Number(draft.temperature),
      routable: draft.routable,
      enabled: draft.enabled
    }),
    onSuccess: () => { toast.success(`Agente "${draft.label || draft.id}" creato.`); onCreated(); onClose(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const idValid = /^[a-z][a-z0-9-]{1,62}$/.test(draft.id.trim());
  const canSubmit = idValid && draft.label.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title="Nuovo agente"
      description="Definisci un agente specializzato. Apparirà ovunque insieme agli altri e l'orchestratore potrà instradargli le richieste pertinenti."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!canSubmit}>Crea agente</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="ID" value={draft.id} onChange={(e) => set({ id: e.target.value })} placeholder="es. groomer"
            hint="2-63 caratteri: lettere minuscole, cifre o trattini, iniziale lettera. Immutabile."
            error={draft.id && !idValid ? 'Formato id non valido.' : undefined} />
          <Input label="Nome" value={draft.label} onChange={(e) => set({ label: e.target.value })} placeholder="es. Agente Toelettatura" />
        </div>
        <Input label="Descrizione" value={draft.description} onChange={(e) => set({ description: e.target.value })}
          hint="Spiega all'orchestratore quando usarlo (argomenti di competenza)." />
        <Textarea label="Istruzioni (system prompt)" rows={5} value={draft.systemPrompt} onChange={(e) => set({ systemPrompt: e.target.value })}
          placeholder="Istruzioni operative dell'agente." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Modello" value={draft.model} onChange={(e) => set({ model: e.target.value })}>
            <option value="">Predefinito{defaultModelId ? ` (${defaultModelId})` : ''}</option>
            {chatModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </Select>
          <Input label="Temperatura" type="number" min="0" max="2" step="0.1" placeholder="Predefinita" value={draft.temperature} onChange={(e) => set({ temperature: e.target.value })} />
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-ink">Abilità (strumenti)</p>
          <ToolGrid toolCatalog={toolCatalog} selected={draft.tools} disabled={false} onToggle={toggleTool} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
          <span className="text-sm text-ink">Instradabile dall'orchestratore</span>
          <Switch checked={draft.routable} onChange={(v) => set({ routable: v })} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2">
          <span className="text-sm text-ink">Attivo</span>
          <Switch checked={draft.enabled} onChange={(v) => set({ enabled: v })} />
        </div>
      </div>
    </Modal>
  );
}

export function AgentsPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasPermission('ai:configure');

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const query = useQuery({ queryKey: ['ai', 'agents'], queryFn: aiApi.getAgents, enabled: hasPermission('ai:configure') });
  const agents = query.data?.agents || [];
  const toolCatalog = query.data?.toolCatalog || [];
  const chatModels = query.data?.chatModels || [];
  const defaultModelId = query.data?.defaultModelId || null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ai', 'agents'] });

  const deleteMutation = useMutation({
    mutationFn: (id) => aiApi.deleteAgent(id),
    onSuccess: () => { toast.success('Agente eliminato.'); setDeleteTarget(null); invalidate(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  if (!hasPermission('ai:configure')) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Agenti" />
        <Card><p className="text-sm text-ink-muted">Permesso <code>ai:configure</code> richiesto.</p></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Agenti"
        description="Per ogni agente dell'assistente: attivazione, modello, temperatura, istruzioni e abilità (inclusa la knowledge base / RAG). Crea agenti personalizzati: l'orchestratore li instrada in automatico."
        actions={canManage ? <Button onClick={() => setCreateOpen(true)}><Plus className="mr-1.5 h-4 w-4" />Nuovo agente</Button> : null}
      />

      {query.isLoading ? (
        <div className="flex flex-col gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 w-full" />)}</div>
      ) : (
        <div className="flex flex-col gap-4">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              toolCatalog={toolCatalog}
              chatModels={chatModels}
              defaultModelId={defaultModelId}
              canManage={canManage}
              onSaved={invalidate}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <CreateAgentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        toolCatalog={toolCatalog}
        chatModels={chatModels}
        defaultModelId={defaultModelId}
        onCreated={invalidate}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Eliminare l'agente?"
        description={deleteTarget ? `L'agente "${deleteTarget.label}" sarà rimosso e non comparirà più nell'instradamento. Operazione irreversibile.` : ''}
        confirmLabel="Elimina"
        destructive
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
